import { motion } from 'framer-motion';
import { BedDouble, Users, LogIn, LogOut, Download, BarChart3 } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { download } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Stat,
  Button,
  Table,
  StatusBadge,
  Avatar,
  Loader,
  EmptyState,
  SectionTitle,
} from '../../components/UI';
import { fmtDateTime, pct } from '../../utils/format';

export default function HotelReport() {
  const { eventId } = useEvents();
  const toast = useToast();

  const { data, loading } = useFetch(
    '/reports/hotel', eventId ? { event: eventId } : {},
  );

  if (loading) return <Loader full label="Building the occupancy report" />;
  if (!data) return <EmptyState message="No report data available" icon={BarChart3} />;

  const s = data.summary;

  return (
    <>
      <SectionTitle
        title="Occupancy report"
        subtitle="Room allocation, check-in and check-out across your property"
        action={
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <EventPicker />
            <Button variant="ghost" icon={Download} disabled={!eventId}
              onClick={() => download('/reports/export', { type: 'rooms', event: eventId }, 'occupancy-report.xlsx')
                .then(() => toast.success('Report downloaded'))
                .catch((e) => toast.error(e.message))}>
              Export
            </Button>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 13, marginBottom: 20 }}>
        <Stat label="Rooms" value={s.rooms} icon={BedDouble} tone="gold" />
        <Stat label="Guests" value={s.guests} icon={Users} tone="info" />
        <Stat label="Occupancy" value={`${s.occupancyRate}%`} icon={BedDouble} tone="success" progress={s.occupancyRate} />
        <Stat label="Checked In" value={s.checkedIn} icon={LogIn} tone="success" />
        <Stat label="Checked Out" value={s.checkedOut} icon={LogOut} tone="info" />
      </div>

      {Object.keys(s.byCategory || {}).length > 0 && (
        <Card style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 15 }}>By room category</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {Object.entries(s.byCategory).map(([cat, v], i) => (
              <motion.div key={cat} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{cat}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-mid)' }}>{v.rooms} rooms · {v.guests} guests</span>
                </div>
                <div style={{ height: 7, background: 'var(--ink-700)', borderRadius: 99, overflow: 'hidden' }}>
                  <motion.div
                    initial={{ width: 0 }} animate={{ width: `${pct(v.rooms, s.rooms)}%` }}
                    transition={{ duration: 0.9, delay: i * 0.07 }}
                    style={{ height: '100%', borderRadius: 99, background: 'linear-gradient(90deg, var(--gold-500), var(--gold-300))' }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </Card>
      )}

      <Table
        rows={data.rows}
        empty="No rooms allocated yet"
        columns={[
          { key: 'roomNumber', title: 'Room', render: (r) => (
            <span style={{ fontWeight: 600, color: r.roomNumber ? 'var(--gold-400)' : 'var(--text-lo)' }}>
              {r.roomNumber || 'Unassigned'}
            </span>
          )},
          { key: 'category', title: 'Category', render: (r) => r.category || '—' },
          { key: 'occupants', title: 'Guests', render: (r) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {r.occupants?.map((o) => (
                <div key={o._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar name={o.name} size={20} vip={o.isVip} />
                  <span style={{ fontSize: 12.3 }}>{o.name}</span>
                </div>
              )) || '—'}
            </div>
          )},
          { key: 'status', title: 'Status', render: (r) => <StatusBadge status={r.status} /> },
          { key: 'checkInAt', title: 'Checked in', render: (r) => (
            <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
              {r.checkInAt ? fmtDateTime(r.checkInAt) : '—'}
            </span>
          )},
          { key: 'checkOutAt', title: 'Checked out', render: (r) => (
            <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
              {r.checkOutAt ? fmtDateTime(r.checkOutAt) : '—'}
            </span>
          )},
        ]}
      />
    </>
  );
}
